/**
 * cartCount
 * ------------------------------------------------------------------------------
 * Keeps every `[data-cart-count]` badge (e.g. the header cart bubble) in sync
 * with the live Shopify cart. The count is server-rendered once via Liquid (see
 * `sections/header-new.liquid`); this module updates it whenever the cart
 * changes without a full page reload.
 *
 * The badge counts TOP-LEVEL lines only — see `countCartItems` below.
 *
 * Cart changes come from two places:
 *   1. Native adds via `@/scripts/components/cartAdd` (calls `refreshCartCount`).
 *   2. Rebuy Smart Cart mutations (add / remove / quantity change), surfaced by
 *      the `rebuy:cart.change` / `rebuy:cart.ready` document events.
 *
 * @namespace cartCount
 */

const CART_COUNT_SELECTOR = '[data-cart-count]';

/**
 * Write a count into every cart badge on the page.
 * @param {number} count
 */
export function setCartCount(count) {
    if (typeof count !== 'number' || Number.isNaN(count)) {
        return;
    }

    document.querySelectorAll(CART_COUNT_SELECTOR).forEach((el) => {
        el.textContent = String(count);
    });
}

/**
 * The cart's own item count, ignoring Shopify nested cart lines.
 *
 * Bundle components and add-ons are added as CHILD line items carrying a
 * `parent_relationship` ({ parent_key }), and Shopify counts every one of them in
 * `cart.item_count` — so a cart holding a single three-component bundle reads as
 * "4". The badge should say what the shopper picked, so we sum the quantities of
 * the top-level lines instead.
 *
 * Falls back to `cart.item_count` when the payload carries no `items` array (a
 * partial cart object from an event), and to `null` when there is nothing usable —
 * `setCartCount` ignores non-numbers, so the badge is left alone rather than
 * blanked. A stale badge beats a wrong one.
 *
 * @param {object} cart Shopify cart (e.g. a `/cart.js` response).
 * @returns {number|null}
 */
export function countCartItems(cart) {
    if (!cart) {
        return null;
    }

    if (!Array.isArray(cart.items)) {
        return typeof cart.item_count === 'number' ? cart.item_count : null;
    }

    return cart.items.reduce((total, item) => {
        if (!item || item.parent_relationship) {
            return total;
        }
        return total + (typeof item.quantity === 'number' ? item.quantity : 0);
    }, 0);
}

/**
 * Fetch the current cart and refresh every badge. Used when we don't already
 * have the updated cart in hand (e.g. after a native `/cart/add.js`).
 * @returns {Promise<void>}
 */
export async function refreshCartCount() {
    try {
        const response = await fetch('/cart.js', {
            headers: {'Content-Type': 'application/json'},
        });
        if (!response.ok) {
            return;
        }
        const cart = await response.json();
        setCartCount(countCartItems(cart));
    } catch (error) {
        /* noop — a stale badge is better than a thrown error */
    }
}

let initialized = false;

/**
 * Wire the Rebuy cart events. Rebuy fires these on `document` with the full
 * Shopify cart in `event.detail.cart`, so we can read `item_count` directly.
 */
export function initCartCount() {
    if (initialized || typeof document === 'undefined') {
        return;
    }
    initialized = true;

    ['rebuy:cart.change', 'rebuy:cart.ready', 'rebuy:cart.add'].forEach((name) => {
        document.addEventListener(name, (event) => {
            const cart = event && event.detail && event.detail.cart;
            const count = countCartItems(cart);
            if (typeof count === 'number') {
                setCartCount(count);
            } else {
                refreshCartCount();
            }
        });
    });
}

if (typeof window !== 'undefined') {
    initCartCount();
}